import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { replaceVariablesInMessage } from '@chatwoot/utils';
import { useOperatingProceduresStore } from 'dashboard/stores/operatingProcedures';
import { NODE_TYPES } from '../helpers/sopFlow';

export function useSopSummary(
  nodes,
  edges,
  simulator,
  cannedResponses,
  messageVariables
) {
  const { t } = useI18n();
  const sopStore = useOperatingProceduresStore();

  const cannedContentFor = node => {
    const automation = node?.data?.automation;
    if (automation?.type !== 'canned_response') return null;
    return (
      cannedResponses.value.find(item => item.id === Number(automation.id))
        ?.content || null
    );
  };

  const summarySteps = computed(() => {
    const path = simulator.state.value.path;
    return path
      .map((nodeId, index) => {
        const node = nodes.value.find(n => n.id === nodeId);
        if (!node) return null;
        const run = simulator.runData.value[nodeId] || {};
        const nextId = path[index + 1];
        let branchLabel = null;
        if (nextId && node.data?.branches?.length) {
          const edge = edges.value.find(
            e => e.source === nodeId && e.target === nextId
          );
          branchLabel =
            node.data.branches.find(b => b.id === edge?.sourceHandle)?.label ||
            null;
        }

        const automationContent =
          node.data?.automation?.type === 'canned_response'
            ? replaceVariablesInMessage({
                message: cannedContentFor(node) || '',
                variables: {
                  ...messageVariables.value,
                  ...(Object.entries(run.variables || {}).reduce(
                    (acc, [key, value]) => ({
                      ...acc,
                      [key]: value,
                      [key.toLowerCase()]: value,
                    }),
                    {}
                  ) || {}),
                },
              })
            : null;

        return {
          key: `${nodeId}-${index}`,
          label: node.data?.label || t(`SOPS.NODES.${node.type.toUpperCase()}`),
          branchLabel,
          checkedItems: (node.data?.items || []).filter(
            (item, itemIndex) => item && run.checked?.[itemIndex]
          ),
          automation: node.data?.automation || null,
          automationContent,
          automationLabel: node.data?.automation?.type
            ? `${t(
                `SOPS.AUTOMATION_TYPES.${node.data.automation.type.toUpperCase()}`
              )}: ${node.data.automation.name}`
            : null,
          referencedSopName:
            node.type === NODE_TYPES.REFERENCE
              ? sopStore.getById(node.data?.operating_procedure_id)?.name ||
                null
              : null,
          confirmed: !!run.confirmed,
          feedbacks: run.feedbacks || [],
        };
      })
      .filter(Boolean);
  });

  return { summarySteps };
}
